import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, FileWarning, ShieldOff } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { SettingsLayout } from './SettingsLayout';
import { downloadPII, sendPIIExport, requestAccountDeletion } from '@/api/settings';

function saveJsonToFile(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function LegalSettingsPage() {
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const downloadMutation = useMutation({
    mutationFn: downloadPII,
    onSuccess: (result) => {
      if (result.success && result.export) {
        saveJsonToFile(result.export, 'user-export.json');
        setMessage('Your data export has been downloaded.');
        setError(null);
      } else {
        setError(result.error || 'Failed to export data');
      }
    },
    onError: () => setError('Failed to export data'),
  });

  const emailMutation = useMutation({
    mutationFn: sendPIIExport,
    onSuccess: (result) => {
      if (result.success) {
        setMessage(result.message || 'Export has been queued and will be emailed to you.');
        setError(null);
      } else {
        setError(result.error || 'Failed to queue export email');
      }
    },
    onError: () => setError('Failed to queue export email'),
  });

  const deletionMutation = useMutation({
    mutationFn: requestAccountDeletion,
    onSuccess: (result) => {
      if (result.success) {
        setMessage(result.message || 'Please contact support to request deletion.');
        setError(null);
      } else {
        setError(result.error || 'Failed to submit deletion request');
      }
    },
    onError: () => setError('Failed to submit deletion request'),
  });

  return (
    <SettingsLayout>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileWarning className="h-5 w-5" />
            Privacy & Data
          </CardTitle>
          <CardDescription>
            Download your data, request exports by email, or initiate account deletion.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {message && (
            <Alert className="bg-green-50 border-green-200">
              <AlertDescription className="text-green-800">{message}</AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Download PII export</p>
            <Button onClick={() => downloadMutation.mutate()} disabled={downloadMutation.isPending}>
              {downloadMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Preparing...
                </>
              ) : (
                'Download JSON export'
              )}
            </Button>
          </div>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Email me a copy</p>
            <Button
              variant="outline"
              onClick={() => emailMutation.mutate()}
              disabled={emailMutation.isPending}
            >
              {emailMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Queuing...
                </>
              ) : (
                'Send export to my email'
              )}
            </Button>
          </div>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Account deletion</p>
            <Button
              variant="destructive"
              onClick={() => deletionMutation.mutate()}
              disabled={deletionMutation.isPending}
            >
              {deletionMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <ShieldOff className="h-4 w-4 mr-2" />
                  Request deletion instructions
                </>
              )}
            </Button>
            <p className="text-xs text-muted-foreground">
              We will provide next steps for account deletion in line with our data retention policy.
            </p>
          </div>
        </CardContent>
      </Card>
    </SettingsLayout>
  );
}
